import { auth as authContracts } from '@personal-blog/contracts';
import type { FormValues } from '@viquard/doc-ui/forms';
import { useCallback } from 'react';
import { z } from 'zod';
import { useResetPassword } from '@/features/auth/api';
import { zodValidate } from '@/lib/zod-validate';
import { useAuthToast } from './use-auth-toast.js';

// `ResetPasswordRequest` now wraps a base object in `attachPasswordMatch`
// (a `superRefine` for confirmPassword === password); `ZodEffects` doesn't
// expose `.pick`, so for the field-level validator we re-bind the canonical
// `Password` schema. Server still re-runs the full request schema.
const FieldSchema = z.object({ password: authContracts.Password });

export interface ResetPasswordController {
  defaults: Record<string, string>;
  validate: ReturnType<typeof zodValidate>;
  submit: (values: FormValues) => Promise<void>;
  isPending: boolean;
  isSuccess: boolean;
}

export function useResetPasswordController(opts: {
  token: string;
  onSuccess?: () => void;
}): ResetPasswordController {
  const reset = useResetPassword();
  const { token, onSuccess } = opts;
  const { notifyError } = useAuthToast();

  const submit = useCallback(
    async (values: FormValues) => {
      const password = typeof values.password === 'string' ? values.password : '';
      const confirmPassword =
        typeof values.confirmPassword === 'string' ? values.confirmPassword : '';
      try {
        const parsed = authContracts.ResetPasswordRequest.parse({
          token,
          password,
          confirmPassword,
        });
        await reset.mutateAsync(parsed);
        onSuccess?.();
      } catch (err) {
        notifyError(err);
      }
    },
    [reset, token, onSuccess, notifyError],
  );

  return {
    defaults: { password: '', confirmPassword: '' },
    validate: zodValidate(FieldSchema),
    submit,
    isPending: reset.isPending,
    isSuccess: reset.isSuccess,
  };
}
